import AddIcon from "@mui/icons-material/Add";
import LoginIcon from "@mui/icons-material/Login";
import {
  Box,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TableRow,
  Tooltip,
} from "@mui/material";
import { Button, Link, Typography } from "@platform/design-system";
import { useSignedInUser } from "@platform/user/client";
import { useState, useEffect, type JSX } from "react";
import { Link as RouterLink, useNavigate } from "react-router";

import {
  SearchInput,
  TableHeadersSort,
  TablePagination,
} from "../../components";
import { ROUTE_NAMES } from "../../constants";

import { BrokerPartner } from "./types";

type Props = {
  brokers: BrokerPartner[];
};

function BrokerPartners({ brokers }: Props): JSX.Element {
  const [filteredBrokers, setFilteredBrokers] = useState(brokers);
  const [searchValue, setSearchValue] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const navigate = useNavigate();
  const offset = page * rowsPerPage;
  const limit = offset + rowsPerPage;
  const { isConsultant } = useSignedInUser();

  useEffect(() => {
    const term = searchValue.trim().toLowerCase();
    if (!term) {
      setFilteredBrokers(brokers);
    } else {
      setFilteredBrokers(
        brokers.filter((c) => c.name.toLowerCase().includes(term)),
      );
    }
    setPage(0);
    // eslint-disable-next-line react-compiler/react-compiler
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [brokers]);

  const handleSearch = ({ target }): void => {
    const newTerm = target.value.trim().toLowerCase() as string;
    setSearchValue(target.value.toLowerCase());
    if (newTerm === "") {
      setFilteredBrokers(brokers);
    } else {
      setFilteredBrokers(
        brokers.filter((c) => c.name.toLocaleLowerCase().includes(newTerm)),
      );
      setPage(0);
    }
  };

  return (
    <>
      <Box mb={3} display="flex" justifyContent="space-between" width="100%">
        <Typography variant="display-medium">Registered Partners</Typography>
        {!isConsultant && (
          <Box
            sx={{
              whiteSpace: "nowrap",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <Button
              variant="primary"
              onClick={() => navigate(`/${ROUTE_NAMES.PRE_REGISTER}`)}
              startIcon={<AddIcon />}
            >
              Add Broker
            </Button>
          </Box>
        )}
      </Box>
      <Box mb={3}>
        {filteredBrokers && (
          <Typography variant="caption">
            Total Broker Partners <b>{filteredBrokers.length}</b>
          </Typography>
        )}
      </Box>
      <Box sx={{ flexGrow: 1 }} mb={4}>
        <SearchInput value={searchValue} onChange={handleSearch} fullWidth />
      </Box>
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableHeadersSort
                headers={[
                  {
                    id: "displayName",
                    label: "Name",
                    isSortable: true,
                  },
                ]}
                data={filteredBrokers}
                setData={setFilteredBrokers}
              />
              <TableCell sx={{ width: 48 }} />
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredBrokers?.length > 0 ? (
              filteredBrokers.slice(offset, limit).map((broker) => (
                <TableRow key={broker.id}>
                  <TableCell padding="none" scope="row">
                    <Link
                      component={RouterLink}
                      to={`/${ROUTE_NAMES.BROKERS}/${broker.id}`}
                      underline="none"
                      sx={{ display: "block", p: 2, width: "100%" }}
                    >
                      <Typography
                        variant="body-medium"
                        component="span"
                        color="inherit"
                      >
                        <strong>{broker.name}</strong>
                      </Typography>
                    </Link>
                  </TableCell>
                  <TableCell sx={{ width: 48 }} padding="none">
                    <Tooltip title="Impersonate broker">
                      <IconButton
                        component="a"
                        href={`/login/CentroWorkplace?impersonating_broker_id=${broker.sfExternalId || broker.externalId}&impersonating_broker_role=agent`}
                        size="small"
                      >
                        <LoginIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={3}>No brokers found</TableCell>
              </TableRow>
            )}
          </TableBody>
          {filteredBrokers?.length > 0 && (
            <TableFooter>
              <TableRow>
                <TablePagination
                  count={filteredBrokers?.length}
                  page={page}
                  setPage={setPage}
                  rowsPerPage={rowsPerPage}
                  setRowsPerPage={setRowsPerPage}
                />
              </TableRow>
            </TableFooter>
          )}
        </Table>
      </TableContainer>
    </>
  );
}

export default BrokerPartners;
